import type { PageContextValue } from "@/modules/jobSearch/components/PageContext/PageContextProvider"

import { type FunctionComponent, useMemo } from "react"
import { NIL } from "uuid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { JobAdsFilters } from "@/modules/companies/client/components/JobAdsFilters/JobAdsFilters"
import { JobAdsWrapper } from "@/modules/companies/client/components/JobAdsWrapper/JobAdsWrapper"
import { RecruitmentPatternInformation } from "@/modules/companies/client/components/RecruitmentPatternInformation/RecruitmentPatternInformation"
import { JOB_ADS_FO_WOOSMAP_MIGRATION } from "@/modules/jobAds/client/constants/flags"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { PageContextProvider } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { useRouter } from "@/modules/routing/hooks/useRouter"
import {
  DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE,
  SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE,
} from "@/modules/search/flags"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"

export const JobAds: FunctionComponent = () => {
  const { push } = useRouter()

  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataJobAds } = useJobAdsFromAlgolia()
  const { dataCurrentUserSearchConfig } = useCurrentUserSearchConfigFetch()

  const searchId = dataJobAds?.searchId || NIL
  const totalSize = dataJobAds?.totalSize || 0
  const pageSize = dataJobAds?.pageSize || 0
  const exhaustiveFacets = dataJobAds?.exhaustiveFacets || true
  const exhaustiveResults = dataJobAds?.exhaustiveResults || true
  const careerCenterId = dataCareerCenter?.id || NIL
  const userId = dataCurrentUser?.id

  const isDirectAlgoliaConnectionEnabled = useFlag(DIRECT_ALGOLIA_CONNECTION_UNLEASH_TOGGLE)
  const isJobAdsFoWoosmapMigrationEnabled = useFlag(JOB_ADS_FO_WOOSMAP_MIGRATION)
  const isSearchProxyV3ConnectionEnabled = useFlag(SEARCH_PROXY_V3_CONNECTION_UNLEASH_TOGGLE)

  const pageContextValue: PageContextValue = useMemo(
    (): PageContextValue => ({
      careerCenterConf: dataCareerCenter?.careerCenterConf || null,
      careerCenterId: dataCareerCenter?.id || null,
      careerCenterLogo: dataCareerCenter?.logo || null,
      careerCenterMainId: dataCareerCenter?.mainCareerCenterId || null,
      careerCenterShortName: dataCareerCenter?.shortName || null,
      currentUserSearchConfig: dataCurrentUserSearchConfig || null,
      facets: dataJobAds?.facets || [],
      hasUserDailyJobOffersAlert: dataCurrentUser?.attributes.dailyJobOffersAlert || false,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isPublicationDateFilterEnabled: false,
      isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled: false,
      isSearchProxyV3ConnectionEnabled,
      isUserLogged: dataCurrentUser?.isLogged || false,
      prefilteredSearchParams: {},
      push,
      savedSearch: null,
      savedSearchSize: 0,
      trackingContext: {
        careerCenterId,
        exhaustiveFacets,
        exhaustiveResults,
        pageSize,
        searchId,
        totalSize,
        userId,
      },
    }),
    [
      careerCenterId,
      dataCareerCenter?.careerCenterConf,
      dataCareerCenter?.id,
      dataCareerCenter?.logo,
      dataCareerCenter?.mainCareerCenterId,
      dataCareerCenter?.shortName,
      dataCurrentUser?.attributes.dailyJobOffersAlert,
      dataCurrentUser?.isLogged,
      dataCurrentUserSearchConfig,
      dataJobAds?.facets,
      exhaustiveFacets,
      exhaustiveResults,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isSearchProxyV3ConnectionEnabled,
      pageSize,
      push,
      searchId,
      totalSize,
      userId,
    ]
  )

  return (
    <PageContextProvider value={pageContextValue}>
      <div data-testid="company_job_ads">
        <JobAdsFilters />
        <JobAdsWrapper />
        <RecruitmentPatternInformation />
      </div>
    </PageContextProvider>
  )
}
